import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { rutasService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Filter, 
  Truck, 
  Download,
  RefreshCw,
  Edit,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  CheckCircle,
  DollarSign,
  MessageSquare
} from 'lucide-react';

// Interfaces
interface CiudadDevolucion {
  ciudad_devolucion: string;
  valor_adicional: number | null;
}

interface CiudadDevolucionEdit {
  ciudad_devolucion: string;
  valor_adicional: string;
  selected: boolean;
}

interface Registro {
  id: number;
  origen_ciudad: string;
  destino_ciudad: string;
  tipo_vehiculo: string;
  tipo_contenedor: string | null;
  tipo_carga: string;
  tipo_carroceria: string;
  viajes_mes: number;
  ton_mes: number;
  valor_de_tarifa: number | null;
  comentarios: string | null;
  porcentaje_intermediacion: number | null;
  costos_adicionales: number | null;
  ciudades_devolucion: CiudadDevolucion[];
  editado_por_mi?: boolean;
}

// Styled Components
const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  background: ${({ theme, variant }) => {
    if (variant === 'secondary') return theme.colors.white;
    if (variant === 'success') return theme.colors.success;
    return theme.colors.primary;
  }};
  color: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.textPrimary : theme.colors.white};
  border: ${({ theme, variant }) => variant === 'secondary' ? `1px solid ${theme.colors.gray300}` : 'none'};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FiltersSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FiltersTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow-x: auto;
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
`;

const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.gray50};
`;

const STICKY_COL_WIDTHS = ['95px', '95px', '85px', '55px', '65px', '75px', '55px', '55px'];
const STICKY_COL_LEFTS = ['0', '95px', '190px', '275px', '330px', '395px', '470px', '525px'];

const TableHeaderCell = styled.th<{ sortable?: boolean; $sticky?: boolean; $stickyIndex?: number }>`
  padding: 6px 8px;
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
  font-size: 11px;
  white-space: nowrap;
  
  ${({ $sticky, $stickyIndex }) => $sticky && $stickyIndex !== undefined && `
    position: sticky;
    left: ${STICKY_COL_LEFTS[$stickyIndex]};
    min-width: ${STICKY_COL_WIDTHS[$stickyIndex]};
    max-width: ${STICKY_COL_WIDTHS[$stickyIndex]};
    z-index: 3;
    background: #f9fafb;
  `}

  ${({ $sticky, $stickyIndex }) => $sticky && $stickyIndex === 7 && `
    box-shadow: 2px 0 4px rgba(0,0,0,0.06);
  `}
  
  ${({ sortable }) => sortable && `
    cursor: pointer;
    user-select: none;
    &:hover { background: rgba(0, 0, 0, 0.05); }
  `}
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $editing?: boolean; $edited?: boolean }>`
  background: ${({ $editing, $edited, theme }) => {
    if ($editing) return `${theme.colors.primary}15`;
    if ($edited) return `${theme.colors.success}10`;
    return theme.colors.white;
  }};

  &:nth-child(even) {
    background: ${({ $editing, $edited, theme }) => {
      if ($editing) return `${theme.colors.primary}15`;
      if ($edited) return `${theme.colors.success}10`;
      return theme.colors.gray50;
    }};
  }
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const TableCell = styled.td<{ $sticky?: boolean; $stickyIndex?: number }>`
  padding: 5px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};

  ${({ $sticky, $stickyIndex }) => $sticky && $stickyIndex !== undefined && `
    position: sticky;
    left: ${STICKY_COL_LEFTS[$stickyIndex]};
    min-width: ${STICKY_COL_WIDTHS[$stickyIndex]};
    max-width: ${STICKY_COL_WIDTHS[$stickyIndex]};
    z-index: 1;
    background: inherit;
  `}

  ${({ $sticky, $stickyIndex }) => $sticky && $stickyIndex === 7 && `
    box-shadow: 2px 0 4px rgba(0,0,0,0.06);
  `}
`;

const EditableInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}30;
  }
`;

const EditableTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-height: 60px;
  resize: vertical;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}30;
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button<{ variant?: 'save' | 'cancel' | 'edit' }>`
  padding: 6px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  ${({ variant, theme }) => {
    switch (variant) {
      case 'save':
        return `
          background: ${theme.colors.success};
          color: white;
          &:hover { background: ${theme.colors.success}dd; }
        `;
      case 'cancel':
        return `
          background: ${theme.colors.error};
          color: white;
          &:hover { background: ${theme.colors.error}dd; }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover { background: ${theme.colors.primaryDark}; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => theme.colors.success}20;
  color: ${({ theme }) => theme.colors.success};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorBanner = styled.div`
  background: ${({ theme }) => theme.colors.error}15;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
`;

const SuccessBanner = styled.div`
  background: ${({ theme }) => theme.colors.success}15;
  border: 1px solid ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.success};
`;

const SortIcon = styled.span`
  margin-left: ${({ theme }) => theme.spacing.xs};
  display: inline-flex;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 20px;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 6px;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.white};
  color: ${({ $active, theme }) => $active ? theme.colors.white : theme.colors.textPrimary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
  min-width: 40px;
  justify-content: center;

  &:disabled {
    background: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.gray400};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.gray50};
  }
`;

const PaginationInfo = styled.div`
  margin-top: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const TarifaDisplay = styled.span<{ $hasValue?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $hasValue, theme }) => $hasValue ? theme.colors.success : theme.colors.textSecondary};
  font-weight: ${({ $hasValue }) => $hasValue ? '600' : '400'};
`;

const ComentarioDisplay = styled.span`
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Componente principal
const RutasModule: React.FC = () => {
  const { proveedor } = useAuth();
  
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Estado para edición
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({
    valor_de_tarifa: '',
    comentarios: '',
    porcentaje_intermediacion: '',
    costos_adicionales: '',
    ciudades_devolucion: [] as CiudadDevolucionEdit[]
  });
  const [saving, setSaving] = useState(false);
  const [loadingCiudades, setLoadingCiudades] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const [sorting, setSorting] = useState({
    sort_by: 'origen_ciudad',
    sort_order: 'ASC' as 'ASC' | 'DESC'
  });

  // Filtros
  const [filtroOrigenCiudad, setFiltroOrigenCiudad] = useState('');
  const [filtroDestinoCiudad, setFiltroDestinoCiudad] = useState('');
  const [filtroTipoVehiculo, setFiltroTipoVehiculo] = useState('');
  const [filtroTipoCarga, setFiltroTipoCarga] = useState('');
  const [filtroTipoCarroceria, setFiltroTipoCarroceria] = useState('');

  const [opcionesFiltros, setOpcionesFiltros] = useState({
    ciudades_origen: [] as string[],
    ciudades_destino: [] as string[],
    tipos_vehiculos: [] as string[],
    tipos_carga: [] as string[],
    tipos_carroceria: [] as string[]
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const cargarDatos = async (pageNumber: number = pagination.current_page) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: pageNumber,
        per_page: pagination.per_page,
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };

      if (proveedor?.username) {
        params.proveedor_username = proveedor.username;
      }

      if (filtroOrigenCiudad) params.origen_ciudad = filtroOrigenCiudad;
      if (filtroDestinoCiudad) params.destino_ciudad = filtroDestinoCiudad;
      if (filtroTipoVehiculo) params.tipo_vehiculo = filtroTipoVehiculo;
      if (filtroTipoCarga) params.tipo_carga = filtroTipoCarga;
      if (filtroTipoCarroceria) params.tipo_carroceria = filtroTipoCarroceria;

      const response = await rutasService.getTablaCompleta(params);
      const data = response.data;

      if (data.registros && Array.isArray(data.registros)) {
        setRegistros(data.registros);
      } else {
        setRegistros([]);
      }

      if (data.pagination) {
        setPagination(data.pagination);
      }

    } catch (error: any) {
      console.error('Error cargando datos:', error);
      setError(error.response?.data?.error || error.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar opciones de filtros solo una vez al inicio
  const cargarOpcionesFiltros = async () => {
    try {
      const response = await rutasService.getOpcionesFiltros();
      const data = response.data;
      setOpcionesFiltros({
        ciudades_origen: data.ciudades_origen || [],
        ciudades_destino: data.ciudades_destino || [],
        tipos_vehiculos: data.tipos_vehiculos || [],
        tipos_carga: data.tipos_carga || [],
        tipos_carroceria: data.tipos_carroceria || []
      });
    } catch (error) {
      console.error('Error cargando opciones de filtros:', error);
    }
  };

  useEffect(() => {
    cargarOpcionesFiltros();
    cargarDatos();
  }, []);

  // Limpiar mensaje de éxito después de 5 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSort = (column: string) => {
    const newOrder = sorting.sort_by === column && sorting.sort_order === 'ASC' ? 'DESC' : 'ASC';
    setSorting({ sort_by: column, sort_order: newOrder });
  };

  // Efecto para recargar datos cuando cambia el ordenamiento
  useEffect(() => {
    if (sorting.sort_by) {
      cargarDatos(1);
    }
  }, [sorting.sort_by, sorting.sort_order]);

  const handleFilterSubmit = () => {
    cargarDatos(1);
  };

  const handleClearFilters = () => {
    setFiltroOrigenCiudad('');
    setFiltroDestinoCiudad('');
    setFiltroTipoVehiculo('');
    setFiltroTipoCarga('');
    setFiltroTipoCarroceria('');
    // Cargar datos directamente sin filtros
    cargarDatosSinFiltros();
  };

  const cargarDatosSinFiltros = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: 1,
        per_page: 50,
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order,
      };

      const response = await rutasService.getTablaCompleta(params);
      const data = response.data;

      if (data.registros && Array.isArray(data.registros)) {
        setRegistros(data.registros);
      } else {
        setRegistros([]);
      }

      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error: any) {
      console.error('Error cargando datos:', error);
      setError(error.response?.data?.error || error.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    cargarDatos(newPage);
  };

  const getSortIcon = (column: string) => {
    if (sorting.sort_by !== column) return null;
    return sorting.sort_order === 'ASC' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  // Funciones de edición
  const handleStartEdit = async (registro: Registro) => {
    setEditingId(registro.id);
    setLoadingCiudades(true);
    
    setEditValues({
      valor_de_tarifa: registro.valor_de_tarifa?.toString() || '',
      comentarios: registro.comentarios || '',
      porcentaje_intermediacion: registro.porcentaje_intermediacion?.toString() || '',
      costos_adicionales: registro.costos_adicionales?.toString() || '',
      ciudades_devolucion: []
    });
    
    try {
      const response = await rutasService.getCiudadesDevolucion({
        origen_ciudad: registro.origen_ciudad,
        destino_ciudad: registro.destino_ciudad,
        tipo_vehiculo: registro.tipo_vehiculo,
        tipo_carga: registro.tipo_carga,
        tipo_carroceria: registro.tipo_carroceria
      });
      const disponibles: string[] = response.data.ciudades || [];
      const seleccionadas = registro.ciudades_devolucion || [];
      
      const ciudadesEdit: CiudadDevolucionEdit[] = disponibles.map(ciudad => {
        const existente = seleccionadas.find(s => s.ciudad_devolucion === ciudad);
        return {
          ciudad_devolucion: ciudad,
          valor_adicional: existente?.valor_adicional?.toString() || '',
          selected: !!existente
        };
      });
      
      setEditValues(prev => ({ ...prev, ciudades_devolucion: ciudadesEdit }));
    } catch (err) {
      console.error('Error cargando ciudades de devolucion:', err);
    } finally {
      setLoadingCiudades(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ valor_de_tarifa: '', comentarios: '', porcentaje_intermediacion: '', costos_adicionales: '', ciudades_devolucion: [] });
  };

  const handleSaveEdit = async (registro: Registro) => {
    if (!proveedor?.username) {
      setError('Debe iniciar sesión para guardar cambios');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const valorTarifa = editValues.valor_de_tarifa ? parseFloat(editValues.valor_de_tarifa) : null;
      const porcIntermed = editValues.porcentaje_intermediacion ? parseFloat(editValues.porcentaje_intermediacion) : null;
      const costosAdic = editValues.costos_adicionales ? parseFloat(editValues.costos_adicionales) : null;

      const ciudadesSeleccionadas = editValues.ciudades_devolucion
        .filter(c => c.selected)
        .map(c => ({
          ciudad_devolucion: c.ciudad_devolucion,
          valor_adicional: c.valor_adicional ? parseFloat(c.valor_adicional) : null
        }));

      await rutasService.guardarPropuestaProveedor({
        proveedor: proveedor.username,
        origen_ciudad: registro.origen_ciudad,
        destino_ciudad: registro.destino_ciudad,
        tipo_vehiculo: registro.tipo_vehiculo,
        tipo_carga: registro.tipo_carga,
        tipo_carroceria: registro.tipo_carroceria,
        valor_propuesto: valorTarifa,
        comentarios: editValues.comentarios || null,
        porcentaje_intermediacion: porcIntermed,
        costos_adicionales: costosAdic,
        ciudades_devolucion: ciudadesSeleccionadas
      });

      setRegistros(prev => prev.map(r => {
        if (r.id === registro.id) {
          return {
            ...r,
            valor_de_tarifa: valorTarifa,
            comentarios: editValues.comentarios || null,
            porcentaje_intermediacion: porcIntermed,
            costos_adicionales: costosAdic,
            ciudades_devolucion: ciudadesSeleccionadas,
            editado_por_mi: true
          };
        }
        return r;
      }));

      setSuccessMessage('¡Propuesta guardada exitosamente!');
      setEditingId(null);
      setEditValues({ valor_de_tarifa: '', comentarios: '', porcentaje_intermediacion: '', costos_adicionales: '', ciudades_devolucion: [] });

    } catch (error: any) {
      console.error('Error guardando propuesta:', error);
      setError(error.response?.data?.error || 'Error al guardar la propuesta');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      
      const params: any = {
        page: 1,
        per_page: 100000,
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };

      if (proveedor?.username) {
        params.proveedor_username = proveedor.username;
      }

      if (filtroOrigenCiudad) params.origen_ciudad = filtroOrigenCiudad;
      if (filtroDestinoCiudad) params.destino_ciudad = filtroDestinoCiudad;
      if (filtroTipoVehiculo) params.tipo_vehiculo = filtroTipoVehiculo;
      if (filtroTipoCarga) params.tipo_carga = filtroTipoCarga;
      if (filtroTipoCarroceria) params.tipo_carroceria = filtroTipoCarroceria;

      const response = await rutasService.getTablaCompleta(params);
      const todosLosRegistros = response.data.registros || [];

      if (todosLosRegistros.length === 0) {
        alert('No hay datos para descargar');
        return;
      }

      const headers = [
        'Ciudad Origen',
        'Ciudad Destino',
        'Tipo Vehículo',
        'Tipo Contenedor',
        'Tipo Carga',
        'Tipo Carrocería',
        'Viajes/Mes',
        'Ton/Mes',
        'Valor Tarifa Propuesto',
        'Comentarios',
        '% Intermediación',
        'Costos Adicionales',
        'Ciudades Devolución / Retiro Vacío'
      ];

      const data = todosLosRegistros.map((r: Registro) => [
        r.origen_ciudad || '',
        r.destino_ciudad || '',
        r.tipo_vehiculo || '',
        r.tipo_contenedor || '',
        r.tipo_carga || '',
        r.tipo_carroceria || '',
        r.viajes_mes?.toString() || '0',
        r.ton_mes?.toString() || '0',
        r.valor_de_tarifa?.toString() || '',
        r.comentarios || '',
        r.porcentaje_intermediacion?.toString() || '',
        r.costos_adicionales?.toString() || '',
        (r.ciudades_devolucion || []).map(c => `${c.ciudad_devolucion}: ${c.valor_adicional != null ? c.valor_adicional : 'N/A'}`).join(' | ') || ''
      ]);

      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(';'),
        ...data.map((row: string[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      ].join('\r\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      const fecha = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `Rutas_COMEX_${fecha}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`✅ Descarga completada! ${todosLosRegistros.length} registros`);

    } catch (error: any) {
      alert(`Error al descargar: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading && registros.length === 0) {
    return (
      <Container>
        <LoadingSpinner>
          <RefreshCw size={24} />
          <span style={{ marginLeft: '12px' }}>Cargando rutas...</span>
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Truck size={32} />
          Rutas COMEX
        </Title>
        <HeaderActions>
          <ActionButton variant="secondary" onClick={handleDownloadExcel} disabled={isDownloading}>
            {isDownloading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download size={16} />
                Descargar Excel
              </>
            )}
          </ActionButton>
          <ActionButton variant="secondary" onClick={() => cargarDatos()}>
            <RefreshCw size={16} />
            Actualizar
          </ActionButton>
        </HeaderActions>
      </Header>

      <FiltersSection>
        <FiltersTitle>
          <Filter size={20} />
          Filtros de Búsqueda
        </FiltersTitle>
        
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Ciudad Origen</FilterLabel>
            <FilterSelect value={filtroOrigenCiudad} onChange={(e) => setFiltroOrigenCiudad(e.target.value)}>
              <option value="">Todas</option>
              {opcionesFiltros.ciudades_origen.map((ciudad) => (
                <option key={ciudad} value={ciudad}>{ciudad}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Ciudad Destino</FilterLabel>
            <FilterSelect value={filtroDestinoCiudad} onChange={(e) => setFiltroDestinoCiudad(e.target.value)}>
              <option value="">Todas</option>
              {opcionesFiltros.ciudades_destino.map((ciudad) => (
                <option key={ciudad} value={ciudad}>{ciudad}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo Vehículo</FilterLabel>
            <FilterSelect value={filtroTipoVehiculo} onChange={(e) => setFiltroTipoVehiculo(e.target.value)}>
              <option value="">Todos</option>
              {opcionesFiltros.tipos_vehiculos.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo Carga</FilterLabel>
            <FilterSelect value={filtroTipoCarga} onChange={(e) => setFiltroTipoCarga(e.target.value)}>
              <option value="">Todos</option>
              {opcionesFiltros.tipos_carga.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo Carrocería</FilterLabel>
            <FilterSelect value={filtroTipoCarroceria} onChange={(e) => setFiltroTipoCarroceria(e.target.value)}>
              <option value="">Todos</option>
              {opcionesFiltros.tipos_carroceria.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>

        <FilterActions>
          <ActionButton variant="secondary" onClick={handleClearFilters}>
            <X size={16} />
            Limpiar
          </ActionButton>
          <ActionButton onClick={handleFilterSubmit}>
            <Search size={16} />
            Aplicar Filtros
          </ActionButton>
        </FilterActions>
      </FiltersSection>

      {successMessage && (
        <SuccessBanner>
          <CheckCircle size={20} />
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </SuccessBanner>
      )}

      {error && (
        <ErrorBanner>
          <AlertCircle size={20} />
          <span>Error: {error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </ErrorBanner>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell sortable $sticky $stickyIndex={0} onClick={() => handleSort('origen_ciudad')}>
                Origen <SortIcon>{getSortIcon('origen_ciudad')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={1} onClick={() => handleSort('destino_ciudad')}>
                Destino <SortIcon>{getSortIcon('destino_ciudad')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={2} onClick={() => handleSort('tipo_vehiculo')}>
                Vehículo <SortIcon>{getSortIcon('tipo_vehiculo')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell $sticky $stickyIndex={3}>
                Cont.
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={4} onClick={() => handleSort('tipo_carga')}>
                Carga <SortIcon>{getSortIcon('tipo_carga')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={5} onClick={() => handleSort('tipo_carroceria')}>
                Carrocería <SortIcon>{getSortIcon('tipo_carroceria')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={6} onClick={() => handleSort('viajes_mes')}>
                Viajes <SortIcon>{getSortIcon('viajes_mes')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable $sticky $stickyIndex={7} onClick={() => handleSort('ton_mes')}>
                Ton <SortIcon>{getSortIcon('ton_mes')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell>
                <DollarSign size={16} style={{ marginRight: 4 }} />
                Valor Tarifa
              </TableHeaderCell>
              <TableHeaderCell>
                <MessageSquare size={16} style={{ marginRight: 4 }} />
                Comentarios
              </TableHeaderCell>
              <TableHeaderCell>% Intermediación</TableHeaderCell>
              <TableHeaderCell>Costos Adicionales</TableHeaderCell>
              <TableHeaderCell>Ciudad Devolución / Retiro Vacío</TableHeaderCell>
              <TableHeaderCell>Acciones</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {registros.length === 0 ? (
              <tr>
                <TableCell colSpan={14}>
                  <EmptyState>
                    <AlertCircle size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                    <p>No se encontraron registros con los filtros aplicados.</p>
                  </EmptyState>
                </TableCell>
              </tr>
            ) : (
              registros.map((registro) => (
                <TableRow 
                  key={registro.id} 
                  $editing={editingId === registro.id}
                  $edited={registro.editado_por_mi}
                >
                  <TableCell $sticky $stickyIndex={0}>{registro.origen_ciudad}</TableCell>
                  <TableCell $sticky $stickyIndex={1}>{registro.destino_ciudad}</TableCell>
                  <TableCell $sticky $stickyIndex={2}>{registro.tipo_vehiculo}</TableCell>
                  <TableCell $sticky $stickyIndex={3}>{registro.tipo_contenedor ?? '-'}</TableCell>
                  <TableCell $sticky $stickyIndex={4}>{registro.tipo_carga}</TableCell>
                  <TableCell $sticky $stickyIndex={5}>{registro.tipo_carroceria}</TableCell>
                  <TableCell $sticky $stickyIndex={6}>{registro.viajes_mes ?? '-'}</TableCell>
                  <TableCell $sticky $stickyIndex={7}>{registro.ton_mes != null ? Number(registro.ton_mes).toFixed(2) : '-'}</TableCell>
                  
                  {/* Columna Valor Tarifa - Editable */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <EditableInput
                        type="number"
                        value={editValues.valor_de_tarifa}
                        onChange={(e) => setEditValues(prev => ({ ...prev, valor_de_tarifa: e.target.value }))}
                        placeholder="Ej: 1500000"
                        autoFocus
                      />
                    ) : (
                      <TarifaDisplay $hasValue={registro.valor_de_tarifa !== null}>
                        {formatCurrency(registro.valor_de_tarifa)}
                        {registro.editado_por_mi && (
                          <EditedBadge>
                            <CheckCircle size={10} />
                            Mi propuesta
                          </EditedBadge>
                        )}
                      </TarifaDisplay>
                    )}
                  </TableCell>
                  
                  {/* Columna Comentarios - Editable */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <EditableTextarea
                        value={editValues.comentarios}
                        onChange={(e) => setEditValues(prev => ({ ...prev, comentarios: e.target.value }))}
                        placeholder="Escriba sus comentarios..."
                      />
                    ) : (
                      <ComentarioDisplay title={registro.comentarios || ''}>
                        {registro.comentarios || '-'}
                      </ComentarioDisplay>
                    )}
                  </TableCell>

                  {/* Columna % Intermediación - Editable */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <EditableInput
                        type="number"
                        step="0.01"
                        value={editValues.porcentaje_intermediacion}
                        onChange={(e) => setEditValues(prev => ({ ...prev, porcentaje_intermediacion: e.target.value }))}
                        placeholder="Ej: 5.5"
                      />
                    ) : (
                      registro.porcentaje_intermediacion != null ? `${registro.porcentaje_intermediacion}%` : '-'
                    )}
                  </TableCell>

                  {/* Columna Costos Adicionales - Editable */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <EditableInput
                        type="number"
                        value={editValues.costos_adicionales}
                        onChange={(e) => setEditValues(prev => ({ ...prev, costos_adicionales: e.target.value }))}
                        placeholder="Ej: 150000"
                      />
                    ) : (
                      formatCurrency(registro.costos_adicionales)
                    )}
                  </TableCell>

                  {/* Columna Ciudad Devolución / Retiro Vacío - Multi-select con valor individual */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <div style={{ minWidth: '220px' }}>
                        {loadingCiudades ? (
                          <span style={{ color: '#888', fontSize: '12px' }}>Cargando...</span>
                        ) : editValues.ciudades_devolucion.length === 0 ? (
                          <span style={{ color: '#888', fontSize: '12px' }}>Sin ciudades disponibles</span>
                        ) : (
                          editValues.ciudades_devolucion.map((cd, idx) => (
                            <div key={cd.ciudad_devolucion} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                              <input
                                type="checkbox"
                                checked={cd.selected}
                                onChange={() => {
                                  setEditValues(prev => {
                                    const updated = [...prev.ciudades_devolucion];
                                    updated[idx] = { ...updated[idx], selected: !updated[idx].selected };
                                    return { ...prev, ciudades_devolucion: updated };
                                  });
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{cd.ciudad_devolucion}</span>
                              {cd.selected && (
                                <EditableInput
                                  type="number"
                                  value={cd.valor_adicional}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setEditValues(prev => {
                                      const updated = [...prev.ciudades_devolucion];
                                      updated[idx] = { ...updated[idx], valor_adicional: val };
                                      return { ...prev, ciudades_devolucion: updated };
                                    });
                                  }}
                                  placeholder="Valor adic."
                                  style={{ width: '100px', fontSize: '11px' }}
                                />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    ) : (
                      <div>
                        {(registro.ciudades_devolucion || []).length > 0 ? (
                          registro.ciudades_devolucion.map(cd => (
                            <div key={cd.ciudad_devolucion} style={{ fontSize: '12px', marginBottom: '2px' }}>
                              <strong>{cd.ciudad_devolucion}</strong>: {cd.valor_adicional != null ? formatCurrency(cd.valor_adicional) : '-'}
                            </div>
                          ))
                        ) : '-'}
                      </div>
                    )}
                  </TableCell>
                  
                  {/* Columna Acciones */}
                  <TableCell>
                    {editingId === registro.id ? (
                      <EditActions>
                        <IconButton 
                          variant="save" 
                          onClick={() => handleSaveEdit(registro)}
                          disabled={saving}
                          title="Guardar"
                        >
                          {saving ? <RefreshCw size={16} /> : <Save size={16} />}
                        </IconButton>
                        <IconButton 
                          variant="cancel" 
                          onClick={handleCancelEdit}
                          disabled={saving}
                          title="Cancelar"
                        >
                          <X size={16} />
                        </IconButton>
                      </EditActions>
                    ) : (
                      <IconButton 
                        variant="edit" 
                        onClick={() => handleStartEdit(registro)}
                        title="Editar propuesta"
                      >
                        <Edit size={16} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {registros.length > 0 && (
        <PaginationInfo>
          Mostrando {registros.length} de {pagination.total_records} registros
          {pagination.total_pages > 1 && (
            <span> - Página {pagination.current_page} de {pagination.total_pages}</span>
          )}
        </PaginationInfo>
      )}

      {pagination.total_pages > 1 && (
        <PaginationContainer>
          <PaginationButton onClick={() => handlePageChange(1)} disabled={pagination.current_page === 1}>
            <ChevronsLeft size={16} />
          </PaginationButton>
          <PaginationButton onClick={() => handlePageChange(pagination.current_page - 1)} disabled={!pagination.has_prev}>
            <ChevronLeft size={16} />
            Anterior
          </PaginationButton>

          {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
            const startPage = Math.max(1, pagination.current_page - 2);
            const page = startPage + i;
            if (page > pagination.total_pages) return null;
            return (
              <PaginationButton key={page} $active={page === pagination.current_page} onClick={() => handlePageChange(page)}>
                {page}
              </PaginationButton>
            );
          })}

          <PaginationButton onClick={() => handlePageChange(pagination.current_page + 1)} disabled={!pagination.has_next}>
            Siguiente
            <ChevronRight size={16} />
          </PaginationButton>
          <PaginationButton onClick={() => handlePageChange(pagination.total_pages)} disabled={pagination.current_page === pagination.total_pages}>
            <ChevronsRight size={16} />
          </PaginationButton>
        </PaginationContainer>
      )}
    </Container>
  );
};

export default RutasModule;
